import { Center } from '@/components/ui/center'
import { Spinner } from '@/components/ui/spinner'
import {
  fetchTableCoversByKeywordThunk,
  resetTableCoversMetaStateOfSearch,
  selectTableCoversMetaState,
} from '@/redux/slices/tableCoverSlice'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import { BaseFileEnum } from '@/typings/domain'
import { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { View } from 'react-native'
import EmptyStateIllustration from '@/components/EmptyStateIllustration'
import TableCoverList from './TableCoverList'

type TableCoversByKeywordProps = {
  keyword: string
}

export default function TableCoversByKeyword({ keyword }: TableCoversByKeywordProps) {
  const { t } = useTranslation('covers')
  const dispatch = useAppDispatch()
  const tableCoversLoaded = useAppSelector(
    state => selectTableCoversMetaState(state, { byKeyword: true }, BaseFileEnum.Form).loaded
  )
  const tableCoverIds = useAppSelector(
    state => selectTableCoversMetaState(state, { byKeyword: true }, BaseFileEnum.Form).coverIds
  )
  const folderCoverIds = useAppSelector(
    state => selectTableCoversMetaState(state, { byKeyword: true }, BaseFileEnum.FormFolder).coverIds
  )
  const isEmpty = tableCoverIds.length === 0 && folderCoverIds.length === 0

  const refetchCovers = useCallback(() => {
    dispatch(fetchTableCoversByKeywordThunk({ keyword, type: BaseFileEnum.Form, reset: true }))
  }, [dispatch, keyword])

  useEffect(() => {
    keyword && refetchCovers()
  }, [keyword, refetchCovers])

  useEffect(() => {
    return () => {
      dispatch(resetTableCoversMetaStateOfSearch())
    }
  }, [dispatch])

  const handleLoadMore = useCallback(
    (type: BaseFileEnum) => {
      dispatch(fetchTableCoversByKeywordThunk({ keyword, type }))
    },
    [dispatch, keyword]
  )

  if (!keyword) {
    return null
  }

  if (!tableCoversLoaded) {
    return (
      <Center className="h-[20vh]">
        <Spinner />
      </Center>
    )
  }

  if (isEmpty) {
    return (
      <View className="items-center justify-center flex-1">
        <EmptyStateIllustration message={t('empty_tables_search')} />
      </View>
    )
  }

  return <TableCoverList byKeyword includeFolder={false} onLoadMore={handleLoadMore} onRefresh={refetchCovers} />
}
